import React, { FunctionComponent, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import styled from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { SelectField } from '../../atoms/formElements/SelectField'
import { Link } from '../../atoms/link/Link'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export const MAX_ADDITIONAL_SUPPORT_SLOTS = 6

export type CareTeamSlotOption = {
  label: string
  value: string
}

export type CareTeamSlot = {
  id: string
  label: string
  options: CareTeamSlotOption[]
  selectedValue?: string
  placeholder?: string
}

export type CareTeamSelectionFormProps = {
  neuropsychTeamSlots: CareTeamSlot[]
  careTeamSlots: CareTeamSlot[]
  alternateNeuropsychTesterSlot?: CareTeamSlot
  onAddAlternateNeuropsychTester?: () => void
  onRemoveAlternateNeuropsychTester?: () => void
  additionalSupportSlots?: CareTeamSlot[]
  onSlotChange: (slotId: string, value: string) => void
  onAddAdditionalSupport?: () => void
  onRemoveAdditionalSupport?: (slotId: string) => void
  testID?: string
}

const FormContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

const SectionCard = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundElement,
  borderRadius: theme.spacing['16px'],
  padding: theme.spacing['16px'],
}))

const SlotGrid = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row' as const,
  flexWrap: 'wrap' as const,
  gap: theme.spacing['24px'],
}))

const SlotContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexGrow: 0,
  flexShrink: 1,
  flexBasis: theme.breakpoints.isMobileSized ? '100%' : 'calc(50% - 12px)',
}))

const AddAlternateLinkContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['16px'],
}))

const SlotHeader = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row' as const,
  justifyContent: 'space-between' as const,
  alignItems: 'center' as const,
  marginBottom: theme.spacing['8px'],
}))

const AdditionalSupportSection = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

const slotBaseInputStyle = { marginBottom: 0 }

const renderSlots = (
  slots: CareTeamSlot[],
  onSlotChange: (slotId: string, value: string) => void,
  testIDPrefix: string,
  onRemove?: (slotId: string) => void,
  removeLabel?: React.ReactNode,
  autoFocus?: boolean,
) =>
  slots.map((slot, index) => (
    <SlotContainer key={slot.id} testID={tID(`${testIDPrefix}-slot-${slot.id}`)}>
      {onRemove ? (
        <SlotHeader>
          <BodyText text={slot.label} testID={tID(`${testIDPrefix}-slot-${slot.id}-label`)} />
          <Link
            text={removeLabel}
            onPress={() => onRemove(slot.id)}
            size={BodyTextSize.SMALL}
            accessibilityLabel={slots.length > 1 ? `Remove ${slot.label} ${index + 1}` : `Remove ${slot.label}`}
            testID={tID(`${testIDPrefix}-slot-${slot.id}-remove`)}
          />
        </SlotHeader>
      ) : null}
      <SelectField
        label={onRemove ? undefined : slot.label}
        value={slot.selectedValue ?? ''}
        onChange={(value: string) => onSlotChange(slot.id, value)}
        options={slot.options}
        placeholder={slot.placeholder}
        name={slot.id}
        accessibilityLabel={slot.label}
        baseInputStyle={slotBaseInputStyle}
        // oxlint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
      />
    </SlotContainer>
  ))

export const CareTeamSelectionForm: FunctionComponent<CareTeamSelectionFormProps> = ({
  neuropsychTeamSlots,
  careTeamSlots,
  alternateNeuropsychTesterSlot,
  onAddAlternateNeuropsychTester,
  onRemoveAlternateNeuropsychTester,
  additionalSupportSlots = [],
  onSlotChange,
  onAddAdditionalSupport,
  onRemoveAdditionalSupport,
  testID,
}) => {
  const baseTestID = testID ?? 'CareTeamSelectionForm'
  const canAddMore = additionalSupportSlots.length < MAX_ADDITIONAL_SUPPORT_SLOTS
  const hasAdditionalSupport = additionalSupportSlots.length > 0

  const focusAlternateOnReveal = useRef(false)

  const removeLabel = (
    <FormattedMessage defaultMessage='Remove' description='Remove button for additional support member dropdown' />
  )

  const handleAddAlternate = () => {
    focusAlternateOnReveal.current = true
    onAddAlternateNeuropsychTester?.()
  }

  const handleRemoveAlternate = () => {
    focusAlternateOnReveal.current = false
    onRemoveAlternateNeuropsychTester?.()
  }

  return (
    <FormContainer testID={tID(baseTestID)}>
      <SectionCard testID={tID(`${baseTestID}-neuropsychTeam`)}>
        <SlotGrid>
          {renderSlots(neuropsychTeamSlots, onSlotChange, `${baseTestID}-neuropsychTeam`)}
          {alternateNeuropsychTesterSlot &&
            renderSlots(
              [alternateNeuropsychTesterSlot],
              onSlotChange,
              `${baseTestID}-neuropsychTeam`,
              handleRemoveAlternate,
              removeLabel,
              focusAlternateOnReveal.current,
            )}
        </SlotGrid>
        {!alternateNeuropsychTesterSlot && onAddAlternateNeuropsychTester && (
          <AddAlternateLinkContainer>
            <Link
              text={
                <FormattedMessage
                  defaultMessage='+ Add optional alternate Neuropsych Tester'
                  description='Link to add an alternate neuropsych tester to the care team'
                />
              }
              size={BodyTextSize.SMALL}
              underline
              onPress={handleAddAlternate}
              testID={tID(`${baseTestID}-addAlternateNeuropsychTester`)}
            />
          </AddAlternateLinkContainer>
        )}
      </SectionCard>
      <SlotGrid testID={tID(`${baseTestID}-careTeam`)}>
        {renderSlots(careTeamSlots, onSlotChange, `${baseTestID}-careTeam`)}
      </SlotGrid>
      {(hasAdditionalSupport || onAddAdditionalSupport) && (
        <AdditionalSupportSection testID={tID(`${baseTestID}-additionalSupport`)}>
          {hasAdditionalSupport && (
            <SlotGrid>
              {renderSlots(
                additionalSupportSlots,
                onSlotChange,
                `${baseTestID}-additionalSupport`,
                onRemoveAdditionalSupport,
                removeLabel,
              )}
            </SlotGrid>
          )}
          {onAddAdditionalSupport && canAddMore && (
            <Link
              text={
                <FormattedMessage
                  defaultMessage='+ Add optional Additional Support member'
                  description='Link to add an additional support member to the care team'
                />
              }
              size={BodyTextSize.SMALL}
              onPress={onAddAdditionalSupport}
              testID={tID(`${baseTestID}-addAdditionalSupport`)}
            />
          )}
        </AdditionalSupportSection>
      )}
    </FormContainer>
  )
}
